/**
 * The player module, resolved lazily and at most once.
 *
 * This file used to open with a top-level
 * `import … from 'https://cdn.vidstack.io/player'`. Because module scripts and
 * deferred classic scripts share one execution list and run in document order, and a
 * module does not execute until its entire import graph has been fetched, Alpine —
 * emitted after this file and listing it as a dependency — could not initialise until
 * that third-party CDN had answered. A slow, filtered or unreachable host therefore
 * took out every interactive element on the site, not just the video.
 *
 * A dynamic import() inside the components that need it cannot block Alpine, and the
 * URL now points at a version-pinned copy vendored in the theme.
 *
 * See docs/media-flow-review.md X1/X2.
 */
let playerModulePromise;

const loadPlayerModule = () => {
	if (!playerModulePromise) {
		const url = window.TR_MEDIA?.PLAYER_URL;

		playerModulePromise = url
			? import(url)
			: Promise.reject(
					new Error(
						'TR_MEDIA.PLAYER_URL is not defined; cannot load the player.'
					)
				);

		// A failed fetch must not poison every later attempt: drop the memo so the
		// next player to come into view retries.
		playerModulePromise.catch(() => {
			playerModulePromise = undefined;
		});
	}

	return playerModulePromise;
};

/**
 * Media Chrome and its provider adapters, each resolved lazily and at most once.
 *
 * Same rule as above: no top-level import, nothing on Alpine's critical path. An
 * un-upgraded custom element creates no iframe and issues no requests, so deferring
 * the module *is* the lazy-loading facade — the poster is a plain `<img>` behind it
 * and the provider is not contacted until a reader scrolls to the player.
 * See docs/media-flow-review.md §9.3.
 */
const moduleCache = new Map();

const loadOnce = (key, urlKey) => {
	if (!moduleCache.has(key)) {
		const url = window.TR_MEDIA?.[urlKey];

		const promise = url
			? import(url)
			: Promise.reject(new Error(`TR_MEDIA.${urlKey} is not defined.`));

		// A failed fetch must not poison every later attempt.
		promise.catch(() => moduleCache.delete(key));
		moduleCache.set(key, promise);
	}

	return moduleCache.get(key);
};

/** The controls, plus whichever adapter this provider needs. */
const loadMediaChrome = (provider) => {
	const providerUrlKey = {
		youtube: 'YOUTUBE_URL',
		vimeo: 'VIMEO_URL',
	}[provider];

	return Promise.all([
		loadOnce('controls', 'CONTROLS_URL'),
		// Self-hosted video is a plain <video>; it needs no adapter.
		providerUrlKey ? loadOnce(provider, providerUrlKey) : Promise.resolve(),
	]);
};

/**
 * Starting one player pauses every other on the page.
 *
 * §10.7 keeps this behaviour; §9.3 predicted the manual `$store.misc.players` registry
 * would disappear with it, and it does. `play` does not bubble, but a capture-phase
 * listener on the document still sees it, because capture propagates from the window
 * down to the target regardless. So one listener replaces a registry that had to be
 * added to, pruned, and iterated — and it cannot go stale.
 */
document.addEventListener(
	'play',
	(event) => {
		const started = event.target;
		if (!started?.pause) return;

		for (const other of document.querySelectorAll(
			'media-controller > [slot="media"]'
		)) {
			/**
			 * `typeof other.pause`, not `other.pause` — a `<youtube-video>` whose
			 * adapter has not loaded yet is still an unupgraded HTMLElement with no
			 * media API at all, and `other.paused` on it is `undefined`, which reads
			 * as "not paused" and would call a method that does not exist.
			 */
			if (other === started || typeof other.pause !== 'function')
				continue;
			if (!other.paused) other.pause();
		}
	},
	true
);

document.addEventListener('alpine:init', () => {
	/**
	 * The Media Chrome player wrapper.
	 *
	 * Almost nothing left to do: the markup is already the player, so this only defers
	 * the module import and pauses on scroll-out. Compare the `videoPlayer` component
	 * below, which has to construct everything imperatively.
	 */
	Alpine.data('mediaChromePlayer', () => ({
		isLoading: false,
		isLoaded: false,

		handleIntersectEnter: async function () {
			if (this.isLoaded || this.isLoading) return;
			this.isLoading = true;

			try {
				await loadMediaChrome(this.$el.dataset.provider);
				this.isLoaded = true;

				/**
				 * youtube-video-element's defaults differ from Vidstack's in two ways
				 * that would show: it forces `cc_load_policy: 1`, burning captions in,
				 * and it omits `disablekb: 1`, so YouTube's iframe competes for
				 * keystrokes with our own controls. `config` is a property, not an
				 * attribute, so it has to be set here — before the element loads.
				 * See docs/vidstack-baseline/README.md.
				 */
				const youtube = this.$el.querySelector('youtube-video');
				if (youtube && !youtube.config) {
					youtube.config = { cc_load_policy: null, disablekb: 1 };
				}

				/**
				 * Only now does this player get a source.
				 *
				 * Defining the adapter upgrades *every* matching element on the page at
				 * once, and an upgraded element with a `src` builds its iframe
				 * immediately — measured, that was 12 YouTube iframes on the homepage
				 * with a single player in view. Holding the URL in `data-src` and
				 * promoting it here is what keeps provider traffic to the players a
				 * reader has actually reached. See docs/media-flow-review.md P5.
				 */
				const media = this.$el.querySelector('[slot="media"]');
				if (media?.dataset.src && !media.getAttribute('src')) {
					media.setAttribute('src', media.dataset.src);
				}
			} catch (error) {
				console.error('Error loading the media player:', error);
			} finally {
				this.isLoading = false;
			}
		},

		handleIntersectLeave: function () {
			this.$el.querySelector('[slot="media"]')?.pause?.();
		},
	}));

	// A commented-out `plyrPlayerThumbnail` component sat here, deprecated 2025-08-14.
	// Plyr was replaced by Vidstack and its bundle is no longer shipped, so the code it
	// referenced could not run. See docs/media-flow-review.md P22.

	Alpine.data('videoPlayer', () => ({
		player: undefined,
		onPlaying: undefined,
		/**
		 * Guards against two `x-intersect:enter` firings racing each other while the
		 * first `await` is still outstanding — `this.player` is not assigned until
		 * creation resolves, so it cannot serve as the re-entry guard on its own.
		 */
		isCreating: false,

		handleIntersectEnter: async function () {
			if (this.player || this.isCreating) return;

			this.isCreating = true;

			const wrapper = this.$el;
			const provider = wrapper.dataset.provider;
			const videoId = wrapper.dataset.videoId;
			const videoUrl = wrapper.dataset.videoUrl;
			const poster = wrapper.dataset.poster;

			try {
				const { VidstackPlayer, VidstackPlayerLayout } =
					await loadPlayerModule();

				const playerConfig = {
					target: wrapper,
					/**
					 * `'idle'` loaded the provider — a YouTube/Vimeo iframe, its
					 * requests and its cookies — during the first idle callback for
					 * every card on the page. `'visible'` keeps the poster and the
					 * perceived responsiveness while paying that cost only for
					 * players the reader has actually scrolled to.
					 * See docs/media-flow-review.md P5 and §7 #3.
					 */
					load: 'visible',
					posterLoad: 'eager',
					viewType: 'video',
					streamType: 'on-demand',
					/**
					 * `crossOrigin: true` on every provider breaks self-hosted MP4s
					 * served without CORS headers. The embedded providers are
					 * iframes and do not need it; only the local `<video>` element
					 * has a same-origin story worth opting into.
					 * See docs/media-flow-review.md P20.
					 */
					crossOrigin: provider !== 'html5',
					playsInline: true,
					poster: poster || '',
					layout: new VidstackPlayerLayout(),
				};

				switch (provider) {
					case 'youtube':
						playerConfig.src = `https://www.youtube.com/embed/${videoId}`;
						break;
					case 'vimeo':
						playerConfig.src = `https://player.vimeo.com/video/${videoId}`;
						break;
					case 'html5':
						playerConfig.src = videoUrl;
						break;
					default:
						// No recognised provider means no source; creating a player
						// with none yields an empty black box.
						return;
				}

				this.player = await VidstackPlayer.create(playerConfig);

				this.$store.misc.players.add(this.player);

				// Handle playing state to pause other videos
				this.onPlaying = () => {
					for (const player of this.$store.misc.players) {
						if (this.player !== player) {
							player.pause();
						}
					}
				};

				this.player.addEventListener('playing', this.onPlaying);
			} catch (error) {
				console.error('Error initializing video player:', error);
			} finally {
				this.isCreating = false;
			}
		},

		handleIntersectLeave: function () {
			if (this.player) {
				this.player.pause();
			}
		},

		/**
		 * Alpine calls this when the component's root leaves the DOM.
		 *
		 * Nothing used to tear a player down: `$store.misc.players` was only ever
		 * pushed to, so every `playing` event iterated a list that included instances
		 * whose DOM had been gone for several pages of infinite scroll, and called
		 * `.pause()` on each. See docs/media-flow-review.md P8/P9.
		 *
		 * Note on the Swiper half of P8: that finding describes slide *clones*
		 * (`.swiper-slide-duplicate`) registering a second player per video. Swiper
		 * dropped cloning in v9 — the bundled build is 11.0.5, which rearranges the
		 * real slide nodes instead — so no duplicate component is ever created. The
		 * move is a detach + re-attach inside one mutation batch, and Alpine skips
		 * destroy/init for nodes present in both its added and removed sets, so it
		 * does not churn this component either. No guard is needed.
		 */
		destroy: function () {
			if (!this.player) return;

			if (this.onPlaying) {
				this.player.removeEventListener('playing', this.onPlaying);
				this.onPlaying = undefined;
			}

			this.$store.misc.players.delete(this.player);
			this.player.destroy();
			this.player = undefined;
		},
	}));

	Alpine.data('audioPlayer', () => ({
		waveSurfer: undefined,
		// Guards the window between the dynamic import() starting and the instance
		// existing, during which `waveSurfer` is still undefined.
		waveSurferLoading: false,
		releaseVolumeWatcher: undefined,
		duration: 0,
		isPlaying: false,
		currentTime: 0,
		volume: 1,
		formatTime(time) {
			const minutes = Math.floor(time / 60);
			const seconds = Math.floor(time % 60);
			return `${minutes.toString().padStart(2, '0')}:${seconds
				.toString()
				.padStart(2, '0')}`;
		},
		/**
		 * Async because WaveSurfer is now fetched on demand.
		 *
		 * It was 28 KB enqueued on every non-lean page, for a component that mounts only
		 * where a post has audio and then only once `x-intersect:enter.once` fires. The
		 * dynamic import() moves it into its own esbuild chunk, fetched at the moment
		 * this function first runs — which is already the moment the waveform is needed,
		 * so nothing observable changes. See docs/js-build-review.md J5c.
		 *
		 * esbuild owns the chunk's name and content hash under `--splitting`, so there is
		 * no URL for PHP to keep in step and no version constant to drift.
		 *
		 * The re-entrancy guard is a promise rather than the old `if (this.waveSurfer)`
		 * check: with an await in the middle, two calls arriving before the import
		 * resolves would both pass a truthiness test on a field that is still undefined
		 * and build two WaveSurfer instances over one container.
		 */
		initWaveSurfer: async function () {
			if (this.waveSurfer || this.waveSurferLoading) return;

			const url = this.$root.dataset.audioUrl;
			const container = this.$root.querySelector(
				'.wave-surfer-container'
			);

			// With no source WaveSurfer resolves the empty string against the current
			// document and downloads the page HTML, then fails to decode it as audio.
			// See docs/media-flow-review.md A2.
			if (!url || !container) return;

			const waveColor = '#888';
			// `--theme-color` is set per category; on a page that does not set it,
			// getPropertyValue() returns '' and WaveSurfer draws no progress at all.
			// See docs/media-flow-review.md A7.
			const progressColor =
				getComputedStyle(this.$root)
					.getPropertyValue('--theme-color')
					.trim() || '#666';

			this.waveSurferLoading = true;

			let WaveSurfer;
			try {
				({ default: WaveSurfer } = await import('wavesurfer.js'));
			} catch (error) {
				// A failed chunk fetch should cost the waveform, not the rest of the
				// component. Leaving waveSurferLoading set would also wedge it shut.
				this.waveSurferLoading = false;
				console.error(
					'Could not load the audio waveform library.',
					error
				);
				return;
			}

			this.waveSurferLoading = false;

			this.waveSurfer = WaveSurfer.create({
				container,
				waveColor,
				progressColor,
				barWidth: 4,
				barGap: 3,
				barAlign: 'bottom',
				normalize: true,
				height: 64,
				url,
			});

			/**
			 * `isPlaying()` is a method, not a property.
			 *
			 * This read `this.waveSurfer.isPlaying`, which stored the unbound function in
			 * Alpine's reactive `isPlaying` field. Alpine invokes any function it finds
			 * while evaluating an expression, binding `this` to the component proxy — so
			 * WaveSurfer's own isPlaying() then ran against the wrong object, read
			 * `this.media.paused` off it, and threw
			 * `TypeError: Cannot read properties of undefined (reading 'paused')` on
			 * every article page carrying audio.
			 *
			 * A freshly created instance has not started playing, and the 'ready' handler
			 * below sets this again anyway, so the honest initial value is simply false.
			 */
			this.isPlaying = false;

			this.waveSurfer.on('ready', () => {
				this.volume = 1;
				this.duration = this.waveSurfer.getDuration();
				this.isPlaying = false;
			});

			this.waveSurfer.on('pause', () => {
				this.isPlaying = false;
			});

			this.waveSurfer.on('play', () => {
				this.isPlaying = true;
			});

			this.waveSurfer.on('click', () => {
				this.waveSurfer.play();
			});

			// $watch returns its own release function; without it the watcher outlives
			// the component. See docs/media-flow-review.md A6.
			this.releaseVolumeWatcher = this.$watch('volume', (value) => {
				this.waveSurfer.setVolume(value);
			});

			this.waveSurfer.on('timeupdate', (currentTime) => {
				this.currentTime = currentTime;
			});
		},
		play: function () {
			this.waveSurfer?.playPause();
		},
		skip: function (second) {
			this.waveSurfer?.skip(second);
		},
		/**
		 * WaveSurfer registers six handlers and holds a decoded copy of the whole
		 * audio file; nothing released either. See docs/media-flow-review.md A6.
		 */
		destroy: function () {
			if (this.releaseVolumeWatcher) {
				this.releaseVolumeWatcher();
				this.releaseVolumeWatcher = undefined;
			}

			if (this.waveSurfer) {
				this.waveSurfer.unAll();
				this.waveSurfer.destroy();
				this.waveSurfer = undefined;
			}
		},
	}));

	Alpine.data('miniPlayerButton', () => ({
		mediaUrl: '',
		thumbnailUrl: '',
		isLoading: false,

		init: function () {
			this.mediaUrl = this.$el.dataset?.mediaUrl ?? '';
			this.thumbnailUrl = this.$el.dataset?.thumbnailUrl ?? '';
		},

		play: async function () {
			if (this.isLoading || !this.mediaUrl) return;

			this.isLoading = true;

			try {
				await loadMediaChrome('html5');

				const wrapper = document.querySelector('#mini-player-wrapper');
				if (!wrapper) return;

				/**
				 * The outgoing player used to be "replaced" with
				 * `wrapper.textContent = ''`, which detaches the DOM but left the
				 * Vidstack instance, its listeners and its media element alive, so the
				 * old audio could keep buffering behind the new one (P9). A custom
				 * element has a native disconnectedCallback, so replacing the markup is
				 * now genuinely enough — pausing first only stops audio a frame earlier.
				 */
				wrapper.querySelector('[slot="media"]')?.pause?.();
				wrapper.replaceChildren();
				wrapper.insertAdjacentHTML('beforeend', this.markup());

				const media = wrapper.querySelector('[slot="media"]');
				this.$store.miniPlayer.isShown = true;
				media?.play?.();
			} catch (error) {
				console.error('Error initializing mini player:', error);
			} finally {
				this.isLoading = false;
			}
		},

		/**
		 * The same flat control set the page players use, minus fullscreen — the mini
		 * player is a fixed 320px box in the corner (§10.7) and has nowhere to expand to.
		 */
		markup: function () {
			const esc = (v) =>
				String(v).replace(
					/[&<>"]/g,
					(c) =>
						({
							'&': '&amp;',
							'<': '&lt;',
							'>': '&gt;',
							'"': '&quot;',
						})[c]
				);

			return `
				<media-controller class="tr-player tr-player--mini">
					<video slot="media" src="${esc(this.mediaUrl)}" playsinline></video>
					${this.thumbnailUrl ? `<media-poster-image slot="poster" src="${esc(this.thumbnailUrl)}" alt=""></media-poster-image>` : ''}
					<media-loading-indicator slot="centered-chrome" noautohide></media-loading-indicator>
					<div slot="centered-chrome" class="tr-player-center">
						<media-play-button class="tr-player-play"></media-play-button>
					</div>
					<media-control-bar class="tr-player-bottom">
						<media-time-display showduration></media-time-display>
						<span class="tr-player-spacer"></span>
						<media-mute-button></media-mute-button>
					</media-control-bar>
					<media-time-range class="tr-player-seek"></media-time-range>
				</media-controller>`;
		},
	}));
});
